/**
 * SelectionManager — pure state container for the viewer's selection.
 *
 * The XtsViewer3D owns a SelectionManager instance internally and exposes
 * selection updates through the controlled `selection` prop and the
 * `onSelectionChange` callback.
 *
 * Selection modes:
 *   - Off    → ignore clicks
 *   - Single → click replaces selection with one item
 *   - Multi  → ctrl/meta-click adds, shift-click range-selects (modules
 *             within the same part only)
 *
 * Range-select is module-only. For movers, shift-click behaves as a
 * plain replacing click — there is no natural "range" along the mover
 * list. Use ctrl/meta for additive mover selection.
 */

import type {
  ModuleRef,
  MoverRef,
  SelectionMode,
  SelectionState,
} from '../types.js';

export type ClickModifiers = {
  ctrl?: boolean;
  shift?: boolean;
  meta?: boolean;
};

export const EMPTY_SELECTION: SelectionState = Object.freeze({
  modules: [],
  movers: [],
});

const moduleKey = (m: ModuleRef) =>
  `${m.processingUnitObjectId}/${m.partObjectId}/${m.moduleIndex}`;

const moverKey = (m: MoverRef) =>
  `${m.processingUnitObjectId}/${m.moverIndex}`;

export function isModuleSelected(
  selection: SelectionState,
  ref: ModuleRef,
): boolean {
  return selection.modules.some(
    (m) =>
      m.processingUnitObjectId === ref.processingUnitObjectId &&
      m.partObjectId === ref.partObjectId &&
      m.moduleIndex === ref.moduleIndex,
  );
}

export function isMoverSelected(
  selection: SelectionState,
  ref: MoverRef,
): boolean {
  return selection.movers.some(
    (m) =>
      m.processingUnitObjectId === ref.processingUnitObjectId &&
      m.moverIndex === ref.moverIndex,
  );
}

export function deselectAll(): SelectionState {
  return EMPTY_SELECTION;
}

export function applyModuleClick(
  selection: SelectionState,
  ref: ModuleRef,
  mode: SelectionMode,
  modifiers: ClickModifiers = {},
): SelectionState {
  if (mode === 'Off') return selection;
  if (mode === 'Single') {
    return { modules: [ref], movers: [] };
  }
  // Multi
  const additive = modifiers.ctrl || modifiers.meta;
  if (modifiers.shift) {
    return rangeSelectModules(selection, ref);
  }
  if (additive) {
    return toggleModuleInSelection(selection, ref);
  }
  // Plain click in Multi mode = replace.
  return { modules: [ref], movers: [] };
}

export function applyMoverClick(
  selection: SelectionState,
  ref: MoverRef,
  mode: SelectionMode,
  modifiers: ClickModifiers = {},
): SelectionState {
  if (mode === 'Off') return selection;
  if (mode === 'Single') {
    return { modules: [], movers: [ref] };
  }
  const additive = modifiers.ctrl || modifiers.meta;
  if (additive) {
    return toggleMoverInSelection(selection, ref);
  }
  return { modules: [], movers: [ref] };
}

export function applyEmptyClick(
  selection: SelectionState,
  mode: SelectionMode,
): SelectionState {
  if (mode === 'Off') return selection;
  return EMPTY_SELECTION;
}

function toggleModuleInSelection(
  selection: SelectionState,
  ref: ModuleRef,
): SelectionState {
  if (isModuleSelected(selection, ref)) {
    return {
      ...selection,
      modules: selection.modules.filter((m) => moduleKey(m) !== moduleKey(ref)),
    };
  }
  return { ...selection, modules: [...selection.modules, ref] };
}

function toggleMoverInSelection(
  selection: SelectionState,
  ref: MoverRef,
): SelectionState {
  if (isMoverSelected(selection, ref)) {
    return {
      ...selection,
      movers: selection.movers.filter((m) => moverKey(m) !== moverKey(ref)),
    };
  }
  return { ...selection, movers: [...selection.movers, ref] };
}

/**
 * Range-select within the most recently selected part: extend the selection
 * to cover all modules from the previous selection's lowest moduleIndex to
 * the new ref's moduleIndex (within the same part).
 *
 * If the selection is empty or spans different parts, falls back to a
 * single-target replace.
 */
function rangeSelectModules(
  selection: SelectionState,
  ref: ModuleRef,
): SelectionState {
  if (selection.modules.length === 0) {
    return { modules: [ref], movers: [] };
  }
  const last = selection.modules[selection.modules.length - 1]!;
  if (
    last.processingUnitObjectId !== ref.processingUnitObjectId ||
    last.partObjectId !== ref.partObjectId
  ) {
    return { modules: [ref], movers: [] };
  }
  const lo = Math.min(last.moduleIndex, ref.moduleIndex);
  const hi = Math.max(last.moduleIndex, ref.moduleIndex);
  const range: ModuleRef[] = [];
  for (let i = lo; i <= hi; i++) {
    range.push({
      processingUnitObjectId: ref.processingUnitObjectId,
      partObjectId: ref.partObjectId,
      moduleIndex: i,
    });
  }
  // Preserve modules from OTHER parts in the previous selection; replace this
  // part's selection with the new range.
  const otherParts = selection.modules.filter(
    (m) =>
      m.processingUnitObjectId !== ref.processingUnitObjectId ||
      m.partObjectId !== ref.partObjectId,
  );
  return { modules: [...otherParts, ...range], movers: [] };
}
